import * as React from 'react';

import { useCollaborationDiagramSync } from 'src/containers/ProjectDiagram/hooks/useCollaborationDiagramSync';
import {
  ContextMenuContext,
  useContextMenuValue,
} from 'src/containers/ProjectDiagram/hooks/useContextMenuValue';
import { useDiagramReadOnly } from 'src/containers/ProjectDiagram/hooks/useDiagramReadOnly';
import {
  SelectionContext,
  useDiagramSelectionValue,
} from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import {
  DiagramContext,
  useDiagramValue,
} from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useHandleDiagramUpdate } from 'src/containers/ProjectDiagram/hooks/useHandleDiagramUpdate';
import { useIsLargeModel } from 'src/containers/ProjectDiagram/hooks/useIsLargeModel';
import {
  TooltipContext,
  useTooltipValue,
} from 'src/containers/ProjectDiagram/hooks/useTooltipValue';
import { useUpdateDiagramData } from 'src/containers/ProjectDiagram/hooks/useUpdateDiagramData';
import { useDiagramSnapshot } from 'src/utils/hooks/useDiagramSnapshot';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type ProjectDiagramContextProviderProps = {
  children: React.ReactNode;
};

export const ProjectDiagramContextProvider: React.VFC<
  ProjectDiagramContextProviderProps
> = ({ children }) => {
  const handleDiagramUpdate = useHandleDiagramUpdate();
  const diagramSnapshot = useDiagramSnapshot();

  const contextMenuValue = useContextMenuValue();
  const selectionValue = useDiagramSelectionValue();
  const tooltipValue = useTooltipValue();

  const [, setContextMenu] = contextMenuValue;
  const [, setSelection] = selectionValue;
  const [, setTooltip] = tooltipValue;

  const handleDiagramSelection = React.useCallback(
    (selection: go.Set<go.Part>) => {
      setSelection(selection.copy());
    },
    [setSelection]
  );

  // Skip building the go.Diagram and edit the model headlessly when the model
  // is large enough that the visual tree would OOM/hang — unless the user has
  // explicitly opted into the diagram via the view toggle
  const isLargeModel = useIsLargeModel();
  const forceDiagram = useReduxSelector(
    (state) => state.ui.modelList.forceDiagram
  );
  const headless = isLargeModel && !forceDiagram;

  // The active edit surface: real diagram or headless facade (see ActiveDiagram).
  const diagram = useDiagramValue({
    diagramSnapshot,
    handleDiagramUpdate,
    handleDiagramSelection,
    headless,
    setContextMenu,
    setTooltip,
  });

  // Applies Redux model-data changes (imported models, added resources) to the
  // active model — must run headless too so imports reach the list view
  useUpdateDiagramData(diagram);

  // Remote/cross-tab merges apply to both surfaces: the list view commits
  // against this model too, so leaving it stale would let the next headless
  // edit overwrite a collaborator's change.
  useCollaborationDiagramSync(diagram);
  // Read-only applies to both surfaces (headless commits gate on it too).
  useDiagramReadOnly(diagram);

  return (
    <DiagramContext.Provider value={diagram}>
      <ContextMenuContext.Provider value={contextMenuValue}>
        <SelectionContext.Provider value={selectionValue}>
          <TooltipContext.Provider value={tooltipValue}>
            {children}
          </TooltipContext.Provider>
        </SelectionContext.Provider>
      </ContextMenuContext.Provider>
    </DiagramContext.Provider>
  );
};
